import { toJS } from 'mobx';
import { Flowstep, Operation, ProviderType, PullMode } from 'shared/types/flowstep';
import { capitalizeString } from 'shared/utils/utils';
import { ValidationErrorsMap } from 'src/v2/states/validation';
import { makeAutoObservableExt } from '@v2/utils/mobx/make-auto-observable-ext';
import { PullEditorState } from '@v2/components/flowsteps/editors/common/states/pull-editor-state';

export type PullObjectsHSErrorKey = 'object-type-not-selected' | 'selected-fields-empty';

export class PullObjectsHSState extends PullEditorState {
  constructor() {
    super({ operation: Operation.PULL, provider: ProviderType.HS });
    makeAutoObservableExt(this, PullObjectsHSState, { validate: false });
  }

  isSimilarFlowstep(flowstep: Flowstep): boolean {
    return flowstep.operation === Operation.PULL && flowstep.provider === ProviderType.HS;
  }

  get validationErrors() {
    const errors = new Map();
    this.validate(errors);
    return errors;
  }

  get isFlowstepButtonsDisabled(): boolean {
    return !this.pullSettings.selectedObject || this.validationErrors.size > 0;
  }

  saveFlowstep(options: { targetFlowId?: string } = {}) {
    return super.saveFlowstep({
      targetFlowId: options.targetFlowId,
      makeCopy: this.pullSettings.createNewSheet && !!this.flowstep?.id,
    });
  }

  get generatedFlowstepName(): string {
    return `Pull objects from ${capitalizeString(this.pullSettings.selectedObject?.name)}`;
  }

  buildFlowstep(): Flowstep {
    const flowstep = super.buildFlowstep();

    return {
      id: this.flowstep?.id,
      flowId: this.flowstep?.flowId,
      name: this.name || this.generatedFlowstepName,
      errorReaction: this.errorReaction,
      operation: Operation.PULL,
      provider: ProviderType.HS,
      version: '4',
      options: {
        ...flowstep.options,
        target: this.pullOptions.startCell,
        worksheetId: this.worksheetId,
        pullMode: this.pullSettings.pullMode,
        withDateTimeColumn: this.pullSettings.withDateTimeColumn,
        createDataTable: this.pullOptions.createExcelTable,
        useDropdownsForPicklists: this.pullOptions.createDropDowns,
      },
      providerOptions: {
        objectType: this.pullSettings.selectedObjectId || '',
        objectProps: toJS(this.pullSettings.selectedFieldNames),
        conditions: toJS(this.pullSettings.conditions),
      },
    };
  }

  override onFlowstepLoaded(flowstep: Flowstep, meta: { isExact?: boolean; flowstepTarget?: any }): void {
    super.onFlowstepLoaded(flowstep, meta);

    const { options = {}, providerOptions = {} } = flowstep;
    const { objectType = '', objectProps = [], conditions = [] } = providerOptions;

    this.pullSettings.createNewSheet = false;
    this.pullSettings.selectedObjectId = objectType;
    this.pullSettings.selectedFieldNames = objectProps;
    this.pullSettings.conditions = conditions;
    this.worksheetId = options.worksheetId;

    this.pullSettings.pullMode = options.pullMode || PullMode.OVERWRITE;
    this.pullSettings.withDateTimeColumn = options.withDateTimeColumn || false;
    this.pullOptions.startCell = options.target || 'A1';
    this.pullOptions.createExcelTable = options.createDataTable || false;
    this.pullOptions.createDropDowns = options.useDropdownsForPicklists || false;
  }

  validate(errors: ValidationErrorsMap<PullObjectsHSErrorKey>): void {
    this.pullSettings.validate(errors);
  }
}
